import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Card, 
  CardContent,
  Paper,
  Stack,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../utils/seo/SEO';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EngineeringIcon from '@mui/icons-material/Engineering';
import Grid from '../utils/GridWrapper';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Home: React.FC = () => {
  const theme = useTheme();

  // Services data
  const services = [
    {
      title: 'Fractional CPO',
      description: 'Strategic product leadership without the full-time commitment, providing executive expertise to drive product vision and operational excellence.',
      icon: <BusinessCenterIcon sx={{ fontSize: 40 }} />
    },
    {
      title: 'C-Level Strategy',
      description: 'Comprehensive strategic planning and guidance for executive leadership to drive business transformation and sustainable growth.',
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />
    },
    {
      title: 'Acquisition Preparation',
      description: 'Expert guidance to prepare your company for acquisition, with a proven track record of successful exits and value maximization.',
      icon: <PsychologyIcon sx={{ fontSize: 40 }} />
    },
    {
      title: 'GenAI Integration',
      description: 'Leverage Generative AI throughout your product lifecycle and business operations to drive innovation, automate processes, and create competitive advantage.',
      icon: <EngineeringIcon sx={{ fontSize: 40 }} />
    }
  ];

  // Testimonials data - Updated with LinkedIn recommendations
  const testimonials = [
    {
      quote: "Tod is the kind of executive every team benefits from: clear-headed, focused, and grounded in both technical expertise and strong interpersonal instincts... His blend of strategic vision, technical depth, and people-first leadership makes him a rare kind of executive... I’d work with Tod again in a heartbeat.",
      author: "Bram Weerts",
      position: "Co-founder Kea Company | Analyst Relations",
    },
    {
      quote: "Tod brings a sharp product vision... coupled with a thoughtful and thorough approach... What really sets Tod apart is his willingness to roll up his sleeves... He’s not only a strategic thinker but also someone you can count on in the trenches. Tod is a huge asset...",
      author: "David Gaik",
      position: "Sr. Director Professional Services @ DataBee",
    },
    {
      quote: "He has that rare combo of being both highly technical and great with people—able to lead teams and work with clients effectively... He stays focused on what truly moves the needle and would be a major asset to any business.",
      author: "Matt Blakely",
      position: "Entrepreneur & Investor. Co-founder of The Dime Lab",
    },
    {
      quote: "Tod took on the critical responsibility of stabilizing and then growing our product and engineering divisions... His strategic vision and execution drove a remarkable turnaround... Tod’s ability to align technical innovation with business goals... made him an invaluable asset... I wholeheartedly recommend him...",
      author: "Ken Naumann",
      position: "CEO - Devo Technology",
    },
    {
      quote: "Tod is one of the most visionary technology leaders I’ve encountered. His ability to anticipate emerging trends... consistently kept us ahead of the curve... He has a rare ability to bridge business strategy with technical execution... Any organization would benefit greatly from his leadership...",
      author: "John Pirc",
      position: "Cybersecurity Product Executive | SaaS Security Strategist",
    },
    {
      quote: "Tod represents a rare confluence of strategic vision and operational acumen... His leadership is characterized by a hands-on, user-centric approach... His expertise transcends traditional CPO boundaries... He would be an invaluable asset to any organization...",
      author: "Ricardo Ramirez",
      position: "Certified Product Manager | Product Operations Manager",
    }
  ];

  return (
    <>
      <SEO
        title="Ewasko Consulting | Fractional CPO & GenAI Strategy | 20+ Years Experience"
        description="Expert fractional CPO services, C-level strategy consulting, Generative AI integration, and acquisition preparation with a proven track record."
        keywords="fractional CPO, generative AI integration, AI product strategy, C-level strategy, acquisition preparation, executive consulting"
        canonicalUrl="https://todfather.com/"
        ogImage="/hero-image.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Ewasko Consulting",
          "url": "https://todfather.com",
          "logo": "https://todfather.com/static/media/ewasko_consulting_cloud_logo.af9351a89ef1183bebd7.png",
          "image": "https://todfather.com/hero-image.jpg",
          "description": "Expert fractional CPO services, C-level strategy consulting, Generative AI integration, and acquisition preparation with a proven track record.",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Orlando",
            "addressRegion": "FL",
            "addressCountry": "US"
          },
          "founder": {
            "@type": "Person",
            "name": "Tod Ewasko",
            "jobTitle": "Fractional CPO & AI Strategy Consultant"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Consulting Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "name": "Fractional CPO",
                "description": "Strategic product leadership without the full-time commitment, providing executive expertise to drive product vision."
              },
              {
                "@type": "Offer",
                "name": "C-Level Strategy",
                "description": "Comprehensive strategic planning and guidance for executive leadership to drive business transformation."
              },
              {
                "@type": "Offer",
                "name": "GenAI Integration",
                "description": "Strategic implementation of Generative AI across your product lifecycle and business operations."
              },
              {
                "@type": "Offer",
                "name": "Acquisition Preparation",
                "description": "Expert guidance to prepare your company for acquisition, with a proven track record of successful exits."
              }
            ]
          },
          "sameAs": [
            "https://www.linkedin.com/in/todewasko/"
          ]
        }}
      />
      <Box>
        {/* Hero Section */}
        <Box 
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'primary.main',
            color: 'white',
            position: 'relative',
            py: { xs: 10, md: 14 },
            overflow: 'hidden'
          }}
        >
          {/* Background decoration */}
          <Box 
            sx={{
              position: 'absolute',
              width: '800px',
              height: '800px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 70%)',
              top: '-400px',
              right: '-200px',
              zIndex: 0
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              width: '600px',
              height: '600px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 70%)',
              bottom: '-300px',
              left: '-100px',
              zIndex: 0
            }}
          />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                >
                  <Typography 
                    variant="overline" 
                    component="div" 
                    sx={{ opacity: 0.8, fontWeight: 'bold', letterSpacing: 2, mb: 1 }}
                  >
                    Ewasko Consulting
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Product Leadership & AI Strategy Consulting
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4,
                      maxWidth: '800px',
                      opacity: 0.9
                    }}
                  >
                    Bringing 20 years of experience to help businesses leverage Generative AI throughout the product lifecycle and achieve operational excellence.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      size="large"
                      component={RouterLink}
                      to="/contact"
                      sx={{ 
                        px: 4, 
                        py: 1.5,
                        fontSize: '1.1rem',
                        boxShadow: '0 8px 15px rgba(0, 0, 0, 0.2)'
                      }}
                    >
                      Schedule a Consultation
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="secondary"
                      size="large"
                      component={RouterLink}
                      to="/services"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2
                        }
                      }}
                    >
                      Our Services
                    </Button>
                  </Stack>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <Box 
                    component="img"
                    src="/hero-image.jpg"
                    alt="Product Management Consulting"
                    sx={{
                      width: '100%',
                      maxWidth: '550px',
                      height: 'auto',
                      display: 'block',
                      mx: 'auto',
                      borderRadius: '12px',
                      border: '4px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Services Section - Modernized */}
        <Box 
          sx={{ 
            py: { xs: 8, md: 10 }, // Reduced vertical padding
            bgcolor: 'background.default',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background decoration */}
          <Box 
            sx={{
              position: 'absolute',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'linear-gradient(145deg, rgba(10,36,99,0.05) 0%, rgba(30,136,229,0.05) 100%)', // Slightly increased opacity
              top: -100,
              right: -200,
              zIndex: 0
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'linear-gradient(145deg, rgba(30,136,229,0.05) 0%, rgba(10,36,99,0.05) 100%)', // Slightly increased opacity
              bottom: -50,
              left: -100,
              zIndex: 0
            }}
          />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}> {/* Reduced margin bottom */}
              <Typography 
                variant="overline" 
                component="div" 
                color="primary" 
                sx={{ 
                  fontWeight: 'bold',
                  letterSpacing: 2,
                  mb: 1
                }}
              >
                Our Expertise
              </Typography>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 'bold',
                  mb: 3,
                  color: 'text.primary',
                  display: 'block'
                }}
              >
                Comprehensive Consulting Services
              </Typography>
              <Typography 
                variant="body1" 
                color="textSecondary" 
                sx={{ 
                  maxWidth: '700px', // Reduced max width
                  mx: 'auto',
                  fontSize: '1.1rem',
                  opacity: 0.9
                }}
              >
                We help businesses transform their product development and strategy with expert guidance based on decades of experience.
              </Typography>
            </Box>
            
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Grid container spacing={3} justifyContent="center"> {/* Reduced spacing between cards */}
                {services.map((service, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div variants={fadeIn}>
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          transition: 'all 0.3s ease',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
                          border: '1px solid rgba(0, 0, 0, 0.04)',
                          background: theme => `linear-gradient(to bottom, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                          '&:hover': { 
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
                            '& .service-icon-wrapper': {
                              transform: 'scale(1.05)',
                              background: theme => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            },
                            '& .service-icon': {
                              color: 'white',
                              transform: 'scale(1.1) rotate(5deg)'
                            },
                            '& .service-title': {
                              color: 'primary.main'
                            }
                          }
                        }}
                        elevation={0}
                      >
                        <Box
                          sx={{
                            p: 3, // Reduced padding
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}
                        >
                          <Box 
                            className="service-icon-wrapper"
                            sx={{ 
                              width: 80, // Slightly smaller icon wrapper
                              height: 80, // Slightly smaller icon wrapper
                              borderRadius: '16px', // Changed to rounded square for modern look
                              background: 'rgba(10,36,99,0.05)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2.5, // Reduced margin
                              transition: 'all 0.4s ease',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                            }}
                          >
                            <Box 
                              className="service-icon"
                              sx={{
                                color: 'primary.main',
                                transition: 'all 0.4s ease'
                              }}
                            >
                              {React.cloneElement(service.icon, { sx: { fontSize: 40 } })}
                            </Box>
                          </Box>
                          <Typography 
                            className="service-title"
                            variant="h6" // Smaller heading size
                            component="h3" 
                            gutterBottom 
                            sx={{ 
                              fontWeight: 'bold',
                              textAlign: 'center',
                              mb: 1.5, // Reduced margin
                              transition: 'color 0.3s ease'
                            }}
                          >
                            {service.title}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="textSecondary"
                            sx={{
                              textAlign: 'center',
                              fontSize: '0.9rem',
                              lineHeight: 1.6,
                              mb: 1.5
                            }}
                          >
                            {service.description}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          mt: 'auto', 
                          borderTop: '1px solid rgba(0,0,0,0.05)', 
                          p: 2,
                          textAlign: 'center',
                          backgroundColor: 'rgba(0,0,0,0.01)'
                        }}>
                          <Button 
                            component={RouterLink}
                            to="/services"
                            color="primary"
                            sx={{ 
                              fontWeight: 'medium',
                              fontSize: '0.85rem',
                              py: 0.5,
                              px: 2,
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: 'rgba(10,36,99,0.05)'
                              }
                            }}
                            endIcon={<span>→</span>}
                          >
                            Learn More
                          </Button>
                        </Box>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
            
            <Box sx={{ textAlign: 'center', mt: 6 }}> {/* Reduced top margin */}
              <Button 
                variant="outlined" 
                color="primary" 
                size="large"
                component={RouterLink}
                to="/services"
                endIcon={<span>→</span>}
                sx={{
                  borderRadius: '30px',
                  px: 4,
                  py: 1.2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    background: 'rgba(10,36,99,0.05)'
                  }
                }}
              >
                Explore All Services
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Recommendations Section */}
        <Box sx={{ 
          py: { xs: 10, md: 12 }, 
          bgcolor: 'primary.dark',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background decoration */}
          <Box 
            sx={{
              position: 'absolute',
              width: '800px',
              height: '800px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 70%)',
              top: -200,
              right: -300,
              zIndex: 0
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              width: '600px',
              height: '600px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 70%)',
              bottom: -200,
              left: -200,
              zIndex: 0
            }}
          />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography 
                variant="overline" 
                component="div" 
                sx={{ 
                  color: 'primary.light', 
                  fontWeight: 'bold',
                  letterSpacing: 2,
                  mb: 1
                }}
              >
                Recommendations
              </Typography>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom 
                color="white"
                sx={{ fontWeight: 'bold', mb: 2 }}
              >
                What Leaders Say
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  maxWidth: '800px', 
                  mx: 'auto',
                  fontSize: '1.05rem'
                }}
              >
                Hear what executives and business leaders have to say about working with Ewasko Consulting.
              </Typography>
            </Box>
            
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Grid container spacing={4} justifyContent="center">
                {testimonials.map((testimonial, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <motion.div variants={fadeIn}>
                      <Paper 
                        sx={{ 
                          p: { xs: 3, md: 4 },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                          position: 'relative',
                          '&::before': {
                            content: '"""',
                            position: 'absolute',
                            top: 12,
                            left: 16,
                            fontFamily: '"Georgia", serif',
                            fontSize: 60,
                            lineHeight: 1,
                            color: 'rgba(10,36,99,0.08)',
                            fontWeight: 700
                          }
                        }}
                        elevation={0}
                      >
                        <Typography 
                          variant="body1" 
                          paragraph
                          sx={{ 
                            mb: 3,
                            fontSize: '1rem',
                            lineHeight: 1.7,
                            fontStyle: 'italic',
                            position: 'relative',
                            pl: 1
                          }}
                        >
                          {testimonial.quote}
                        </Typography>
                        <Box 
                          sx={{ 
                            mt: 'auto', 
                            pt: 2,
                            display: 'flex', 
                            alignItems: 'center', 
                            borderTop: '1px solid rgba(0,0,0,0.06)'
                          }}
                        >
                          <Box sx={{ ml: 0 }}>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight="bold"
                              color="primary.main"
                            >
                              {testimonial.author}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {testimonial.position}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Box>
        
        {/* About Section */}
        <Box sx={{ 
          py: { xs: 10, md: 12 }, 
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background decoration */}
          <Box 
            sx={{
              position: 'absolute',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background: 'linear-gradient(145deg, rgba(10,36,99,0.02) 0%, rgba(30,136,229,0.02) 100%)',
              top: -50,
              left: -100,
              zIndex: 0
            }}
          />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={8} alignItems="center">
              <Grid item xs={12} md={5}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Box 
                    sx={{
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: '80%',
                        height: '80%',
                        borderRadius: '12px',
                        background: theme.palette.primary.main,
                        bottom: -20,
                        right: -20,
                        zIndex: -1,
                        opacity: 0.1
                      }
                    }}
                  >
                    <Box 
                      component="img"
                      src="/presentation-image.png"
                      alt="Ewasko Consulting Expert"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '12px',
                        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.12)',
                        border: '4px solid white'
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={7}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Typography 
                    variant="overline" 
                    component="div" 
                    color="primary" 
                    fontWeight="bold"
                    sx={{ letterSpacing: 2, mb: 1 }}
                  >
                    About Us
                  </Typography>
                  <Typography 
                    variant="h3" 
                    component="h2" 
                    gutterBottom
                    sx={{ fontWeight: 'bold', mb: 3 }}
                  >
                    20 Years of Industry Excellence
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ fontSize: '1.05rem', lineHeight: 1.7, mb: 2 }}
                  >
                    With two decades of experience in product leadership, strategy development, and engineering management, Ewasko Consulting brings unparalleled expertise to help businesses achieve their objectives.
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ fontSize: '1.05rem', lineHeight: 1.7, mb: 3 }}
                  >
                    We've helped companies across various industries refine their product strategies, optimize operations, and achieve sustainable growth through customer-centric approaches and data-driven methodologies.
                  </Typography>
                  
                  {/* Key expertise points */}
                  <Box 
                    sx={{ 
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mt: 4,
                      mb: 4
                    }}
                  >
                    {['Product Leadership', 'Generative AI Strategy', 'Technical Innovation', 'Acquisition Success'].map((item) => (
                      <Box 
                        key={item}
                        sx={{ 
                          px: 2.5, 
                          py: 1.2,
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: '30px',
                          color: 'primary.main',
                          fontWeight: 'medium',
                          fontSize: '0.95rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                  
                  <Box sx={{ mt: 4 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      size="large"
                      component={RouterLink}
                      to="/about"
                      endIcon={<span>→</span>}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.05rem',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      Learn More About Us
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
        
        {/* YouTube Interview Section */}
        <Box sx={{ 
          py: { xs: 10, md: 12 }, 
          bgcolor: 'background.default',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="overline" 
                component="div" 
                color="primary" 
                sx={{ 
                  fontWeight: 'bold',
                  letterSpacing: 2,
                  mb: 1
                }}
              >
                Featured Interview
              </Typography>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 3 }}
              >
                Industry Insights & Expertise
              </Typography>
              <Typography 
                variant="body1" 
                color="textSecondary" 
                sx={{ 
                  maxWidth: '800px', 
                  mx: 'auto',
                  mb: 5,
                  fontSize: '1.05rem'
                }}
              >
                Watch Tod Ewasko share insights on product leadership, industry trends, and strategic innovation approaches.
              </Typography>
            </Box>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Box 
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '860px',
                  mx: 'auto',
                  boxShadow: '0 15px 45px rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    bottom: -8,
                    right: -8,
                    background: theme.palette.primary.main,
                    borderRadius: '12px',
                    opacity: 0.2,
                    zIndex: -1
                  }
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '56.25%' // 16:9 aspect ratio
                  }}
                >
                  <Box
                    component="iframe"
                    src="https://www.youtube.com/embed/dH7g1zG4uUQ"
                    title="Tod Ewasko Product Leadership Interview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px',
                      border: '4px solid white'
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    mb: 1,
                    fontSize: '0.95rem'
                  }}
                >
                  Read about the recent successful exits:
                </Typography>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  justifyContent="center"
                >
                  <Button
                    component="a"
                    href="https://www.prnewswire.com/news-releases/partnerone-acquires-netwitness-from-rsa-302403433.html"
                    target="_blank"
                    rel="noopener"
                    color="primary"
                    size="small"
                    endIcon={<span>→</span>}
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 'medium',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    PartnerOne Acquires NetWitness from RSA
                  </Button>
                  <Button
                    component="a"
                    href="https://www.prnewswire.com/news-releases/exterro-acquires-accessdata-to-form-the-leading-enterprise-legal-grc-software-platform-across-data-privacy-forensics-and-e-discovery-301185340.html"
                    target="_blank"
                    rel="noopener"
                    color="primary"
                    size="small"
                    endIcon={<span>→</span>}
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 'medium',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Exterro Acquires AccessData
                  </Button>
                </Stack>
              </Box>
            </motion.div>
          </Container>
        </Box>
        
        {/* In the News Section */}
        <Box sx={{ 
          py: { xs: 8, md: 10 },
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="overline" 
                component="div" 
                color="primary" 
                sx={{ 
                  fontWeight: 'bold',
                  letterSpacing: 2,
                  mb: 1
                }}
              >
                Industry Recognition
              </Typography>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 2 }}
              >
                In the News
              </Typography>
              <Typography 
                variant="body1" 
                color="textSecondary" 
                sx={{ 
                  maxWidth: '800px', 
                  mx: 'auto',
                  mb: 3,
                  fontSize: '1.05rem'
                }}
              >
                Featured insights, interviews, and contributions to industry publications and thought leadership.
              </Typography>
            </Box>
            
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              {/* Changed to a grid layout instead of a list */}
              <Grid container spacing={2} justifyContent="center">
                { [
                  {
                    title: "AWS AppFabric Teaches Us an Important Lesson About the App Ecosystem",
                    source: "CIO Influence",
                    url: "https://cioinfluence.com/application-security/aws-appfabric-teaches-us-an-important-lesson-about-the-app-ecosystem/"
                  },
                  {
                    title: "Tod Ewasko Named Chief Product Officer",
                    source: "NetWitness",
                    url: "https://www.netwitness.com/news/tod-ewasko-named-chief-product-officer/"
                  },
                  {
                    title: "Tod Ewasko, Civix VP of DevOps, on Modernizing GovTech",
                    source: "GoCivix",
                    url: "https://gocivix.com/news/tod-ewasko-civix-vp-of-devops-on-modernizing-govtech/"
                  },
                  {
                    title: "Shifting to SaaS: A Product Development Innovation Journey",
                    source: "eRepublic Webinars",
                    url: "https://webinars.erepublic.com/agenda/okta.com/okta.com/%7B%7Bevent.external_form_url%7D%7D/Shifting-to-SaaS%3A-A-Product-Development-Innovation-Journey--141142.html"
                  },
                  {
                    title: "Nozomi Networks and NetWitness Team to Deliver Advanced Cyber Security Solutions",
                    source: "Nozomi Networks",
                    url: "https://www.nozominetworks.com/press-release/nozomi-networks-and-netwitness-team-to-deliver-advanced-cyber-security-solutions-to-ot-iot-environments-across-critical-infrastructure-systems"
                  },
                  {
                    title: "Interview with Tod Ewasko, Director of Product Management",
                    source: "Forensic Focus",
                    url: "https://www.forensicfocus.com/interviews/tod-ewasko-director-of-product-management-accessdata/"
                  },
                  {
                    title: "NetWitness - Black Hat Sponsor Post",
                    source: "Black Hat",
                    url: "https://www.blackhat.com/sponsor-posts/08282023-netwitness.html"
                  },
                  {
                    title: "Challenges for Security Teams with a Remote Workforce",
                    source: "NASDAQ",
                    url: "https://www.nasdaq.com/videos/challenges-for-security-teams-with-a-remote-workforce"
                  },
                  {
                    title: "NetWitness Strengthens Groundbreaking Security Data Governance Capabilities",
                    source: "Help Net Security",
                    url: "https://www.helpnetsecurity.com/2023/10/12/netwitness-sdg/"
                  },
                  {
                    title: "Advancing OT Security with Smart IoT",
                    source: "Future IoT",
                    url: "https://futureiot.tech/advancing-ot-security-with-smart-iot/"
                  },
                  {
                    title: "NetWitness 12.3 Update: Enhanced User Experience and Managed Detection",
                    source: "Help Net Security",
                    url: "https://www.helpnetsecurity.com/2023/10/17/netwitness-12-3-update/"
                  },
                  {
                    title: "E-Discovery Challenges in BYOD Programs",
                    source: "In-House Ops",
                    url: "https://www.inhouseops.com/2018/12/e-discovery-challenges-byod-programs/"
                  },
                  {
                    title: "NetWitness Provides More Visibility Into Cyber Threats",
                    source: "MSSP Alert",
                    url: "https://www.msspalert.com/news/netwitness-provides-more-visibility-into-cyber-threats"
                  },
                  {
                    title: "NetWitness Featured as Industry Leader in NDR and SIEM in 2023 GigaOm Radar Reports",
                    source: "Business Wire India",
                    url: "https://www.businesswireindia.com/netwitness-featured-as-industry-leader-in-ndr-and-siem-in-2023-gigaom-radar-reports-87277.html"
                  },
                  {
                    title: "UNF Basketball Star Demarcus Daniels Leads Athletics Hall of Fame Class of 2023",
                    source: "The Florida Times-Union",
                    url: "https://www.jacksonville.com/story/sports/college/basketball/2022/12/10/unf-basketball-star-demarcus-daniels-leads-athletics-hall-of-fame-class-of-2023/69717288007/"
                  },
                  {
                    title: "Encryption Management & Computer Forensics",
                    source: "WinMagic",
                    url: "https://winmagic.com/en/encryption-management-computer-forensic/"
                  },
                  {
                    title: "NetWitness Brings Enterprise-Level Security to Remote Workforces",
                    source: "Forbes",
                    url: "https://www.forbes.com/sites/waynerash/2023/04/06/netwitness-brings-enterprise-level-security-to-remote-workforces/"
                  },
                  {
                    title: "AccessData Expert on the Way Forward for Digital Forensics",
                    source: "Intelligent CIO",
                    url: "https://www.intelligentciso.com/2019/07/30/accessdata-expert-on-the-way-forward-for-digital-forensics/"
                  }
                ].map((article, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        height: '100%',
                        p: 2,
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        border: '1px solid rgba(0,0,0,0.05)',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                          borderColor: 'rgba(10,36,99,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 'medium', 
                          mb: 1,
                          lineHeight: 1.3,
                          height: '3.9em',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {article.title}
                      </Typography>
                      <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'inline-flex',
                            bgcolor: 'rgba(10,36,99,0.06)', 
                            px: 1, 
                            py: 0.3, 
                            borderRadius: '4px',
                            fontWeight: 'medium',
                            color: 'primary.main',
                            fontSize: '0.75rem'
                          }}
                        >
                          {article.source}
                        </Typography>
                        <Button
                          component="a"
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          size="small"
                          sx={{ 
                            minWidth: 'auto',
                            p: 0.5
                          }}
                        >
                          →
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              {/* Add a View All button */}
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  component="a"
                  href="/about"
                  endIcon={<span>→</span>}
                  sx={{
                    borderRadius: '30px',
                    px: 3,
                    py: 1,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      background: 'rgba(10,36,99,0.05)'
                    }
                  }}
                >
                  View More Press Coverage
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>
        
        {/* CTA Section */}
        <Box sx={{ 
          py: { xs: 10, md: 12 }, 
          bgcolor: 'background.default', 
          textAlign: 'center',
          position: 'relative', 
          overflow: 'hidden'
        }}>
          {/* Background decoration */}
          <Box 
            sx={{
              position: 'absolute',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'linear-gradient(145deg, rgba(10,36,99,0.02) 0%, rgba(30,136,229,0.02) 100%)',
              top: -100,
              left: -100,
              zIndex: 0
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background: 'linear-gradient(145deg, rgba(30,136,229,0.02) 0%, rgba(10,36,99,0.02) 100%)',
              bottom: -100,
              right: -100,
              zIndex: 0
            }}
          />
          
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 4,
                  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.08)',
                  p: { xs: 4, md: 6 },
                  border: '1px solid rgba(0,0,0,0.03)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -5,
                    left: -5,
                    right: -5,
                    bottom: -5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    borderRadius: 4,
                    opacity: 0.05,
                    zIndex: -1
                  }
                }}
              >
                <Typography 
                  variant="overline" 
                  component="div" 
                  color="primary" 
                  fontWeight="bold"
                  sx={{ letterSpacing: 2, mb: 1 }}
                >
                  Take the Next Step
                </Typography>
                <Typography 
                  variant="h3" 
                  component="h2" 
                  gutterBottom
                  sx={{ fontWeight: 'bold', mb: 3 }}
                >
                  Ready to Transform Your Product Strategy?
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  color="textSecondary" 
                  sx={{ 
                    mb: 4, 
                    maxWidth: '700px', 
                    mx: 'auto',
                    fontSize: '1.05rem',
                    lineHeight: 1.7
                  }}
                >
                  Schedule a consultation today and discover how Ewasko Consulting can help your business achieve product excellence and operational efficiency through strategic leadership and Generative AI integration.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  component={RouterLink}
                  to="/contact"
                  sx={{ 
                    py: 1.8, 
                    px: 5, 
                    fontSize: '1.1rem',
                    borderRadius: '30px',
                    boxShadow: '0 8px 25px rgba(10,36,99,0.15)'
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Home;